import React, { useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmDialog } from "primereact/confirmdialog";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import UiContext from "../../../store/ui-context";
import VerifAuthorizationDialog from "../../../dialogs/VerifAuthorizationDialog";
import useEditorState from "../hooks/page/useEditorState";
import useEditorVerifSetting from "../hooks/signature/useEditorVerifSetting";
import useEditorHeaderApi from "../hooks/header/useEditorHeaderApi";
import useEditorDetilApi from "../hooks/checklist/useEditorDetilApi";
import useEditorImageApi from "../hooks/foto/useEditorImageApi";
import useEditorVerify from "../hooks/signature/useEditorVerify";
import useEditorVerifyValidation from "../hooks/signature/useEditorVerifyValidation";
import useEditorInitialLoad from "../hooks/page/useEditorInitialLoad";
import useEditorTitle from "../hooks/page/useEditorTitle";
import useEditorPermission from "../hooks/page/useEditorPermission";
import useEditorSelectorDialogs from "../hooks/header/useEditorSelectorDialogs";
import useEditorCamera from "../hooks/foto/useEditorCamera";
import useEditorMarking from "../hooks/foto/useEditorMarking";
import useEditorSignature from "../hooks/signature/useEditorSignature";
import useEditorNote from "../hooks/checklist/useEditorNote";
import useEditorNavigation from "../hooks/page/useEditorNavigation";
import useEditorFooterActions from "../hooks/page/useEditorFooterActions";
import { getFotoFolderName } from "../utils/editorHelpers";
import EditorPopover from "./checklist/EditorPopover";
import EditorNoteDialog from "./checklist/EditorNoteDialog";
import EditorFotoUploader from "./checklist/EditorFotoUploader";
import EditorSelectorDialogs from "./dialogs/EditorSelectorDialogs";
import EditorHeaderCard from "./header/EditorHeaderCard";
import EditorActionBar from "./actions/EditorActionBar";
import EditorChecklistSection from "./checklist/EditorChecklistSection";
import EditorSignatureSection from "./signature/EditorSignatureSection";
import EditorFooterActions from "./actions/EditorFooterActions";

/**
 * The editor page shared by the mobil and the trado editor.
 *
 * `variant` is everything that differs between the two (see cek-keluar-masuk-mobil-editor / cek-keluar-masuk-trado-editor):
 *   config                    values read by the shared hooks (see editorConfig.js)
 *   useTrailerDialogs         hook: state + handlers of the trailer selector(s)
 *   useValidation             hook: what has to be true before verifying / saving
 *   HeaderTopRows             header rows with tanggal / jam, the vehicle and the trailer field(s)
 *   TrailerDialogs            trailer selector dialog(s)
 *   TrailerCheckList          checklist card of the trailer
 *   FotoSections              photo sections below the checklist
 *   buildSignatureValidators  which signature boxes validate first: (validation) => ({ onValidateXxx })
 */
const EditorMain = ({ variant }) => {
  const {
    config,
    useTrailerDialogs,
    useValidation,
    HeaderTopRows,
    TrailerDialogs,
    TrailerCheckList,
    FotoSections,
    buildSignatureValidators,
  } = variant;

  const { id } = useParams();
  const uiContext = useContext(UiContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUrl = location.pathname;
  const employee = useSelector((state) => state.auth.employee);
  const sideMenuIsVisible = useSelector((state) => state.ui.sideMenuIsVisible);
  const { requestGet, requestGetOne, requestPost, requestDelete, loading } =
    useAPIRequest();
  const permissions = useSelector((state) => state.auth.permissions);

  const [isPnjTransport, setIsPnjTransport] = useState(
    permissions.includes("view pnj-transport")
  );

  // shared dependencies handed to every hook
  const ctx = {
    id,
    uiContext,
    navigate,
    dispatch,
    location,
    currentUrl,
    employee,
    permissions,
    requestGet,
    requestGetOne,
    requestPost,
    requestDelete,
  };

  // data state
  const state = useEditorState({
    currentUrl,
    berangkatUrlPart: config.berangkatUrlPart,
  });
  const {
    isBerangkat,
    modeAmbilGambar,
    setModeAmbilGambar,
    headerData,
    setHeaderData,
    headerError,
    showBerangkat,
    setShowBerangkat,
    pengecekanBerangkatData,
    setPengecekanBerangkatData,
    pengecekanKembaliData,
    setPengecekanKembaliData,
    pengecekanBerangkatDetilData,
    setPengecekanBerangkatDetilData,
    pengecekanKembaliDetilData,
    setPengecekanKembaliDetilData,
    pengecekanPertama,
    listProblemData,
    setListProblemData,
  } = state;

  // API call
  const verifSetting = useEditorVerifSetting(ctx);
  const { verifSettingPengecekan } = verifSetting;
  const headerApi = useEditorHeaderApi({ ...ctx, ...state, ...config });
  const detilApi = useEditorDetilApi({ ...ctx, ...state });
  const imageApi = useEditorImageApi({ ...ctx, ...state, ...detilApi });
  const verifyApi = useEditorVerify({ ...ctx, ...state });

  // first time initializations
  useEditorInitialLoad({ ...ctx, ...state, ...headerApi, ...verifSetting });
  useEditorTitle({ ...ctx, ...state });
  const { permittedToEdit } = useEditorPermission({ ...ctx, ...state });

  // handle UI event section
  const selectorDialogs = {
    ...useEditorSelectorDialogs({ ...state }),
    ...useTrailerDialogs({ ...state }),
  };
  const camera = useEditorCamera({ ...imageApi });
  const marking = useEditorMarking({ ...ctx, ...state, ...detilApi, ...config });
  const signature = useEditorSignature({
    ...ctx,
    ...state,
    ...verifyApi,
    ...headerApi,
  });
  const validation = useValidation({ ...ctx, ...state, ...detilApi });
  const note = useEditorNote({
    permittedToEdit,
    isBerangkat,
    pengecekanBerangkatDetilData,
    setPengecekanBerangkatDetilData,
    setPengecekanKembaliDetilData,
    ...detilApi,
  });
  const navigation = useEditorNavigation({ ...ctx, ...state, ...headerApi });
  const footerActions = useEditorFooterActions({ ...state, ...config });

  const { handleOnClickReload } = navigation;
  const { handleOnClickDeskripsi } = note;
  const { handleOnClickSaveCheckList } = validation;

  // props shared by the photo sections
  const fotoProps = {
    listProblemData,
    headerData,
    isBerangkat,
    showBerangkat,
    setShowBerangkat,
    permittedToEdit,
    loading,
    pengecekanBerangkatData,
    pengecekanKembaliData,
    pengecekanBerangkatDetilData,
    pengecekanKembaliDetilData,
    setPengecekanBerangkatDetilData,
    setPengecekanKembaliDetilData,
    handleOnClickReload,
    handleOnClickCameraPreview: camera.handleOnClickCameraPreview,
    folderName: getFotoFolderName(headerData),
    // Save (Foto Kendaraan) is blocked on the kembali page until Speedometer Kembali is filled in, same rule
    // as Save Bagian on the checklist
    saveDisabled:
      !isBerangkat && (!headerData.speedo_akhir || headerData.speedo_akhir <= 0),
    handleOnClickAddMarking: marking.handleOnClickAddMarking,
    handleOnChangeKordinat: marking.handleOnChangeKordinat,
    handleOnClickRemoveMarking: marking.handleOnClickRemoveMarking,
    handleOnClickSaveMarkingList: validation.handleOnClickSaveMarkingList,
  };

  // every signature box first runs the shared check (header / checklist / foto saved, Foto Asli taken, no unsaved
  // change), then the variant's own check when it has one for that box
  const { validateBeforeVerify } = useEditorVerifyValidation({
    ...ctx,
    ...state,
    hasTrailer: config.hasTrailer,
  });
  const variantValidators = buildSignatureValidators(validation);
  const withSharedValidation = (side, variantValidate) => () =>
    validateBeforeVerify(side) && (variantValidate ? variantValidate() : true);
  const signatureValidators = {
    onValidateStaffBerangkat: withSharedValidation(
      "berangkat",
      variantValidators.onValidateStaffBerangkat
    ),
    onValidateKeamananBerangkat: withSharedValidation(
      "berangkat",
      variantValidators.onValidateKeamananBerangkat
    ),
    onValidateStaffKembali: withSharedValidation(
      "kembali",
      variantValidators.onValidateStaffKembali
    ),
    onValidateKeamananKembali: withSharedValidation(
      "kembali",
      variantValidators.onValidateKeamananKembali
    ),
  };

  return (
    <div className={`col-start-start px-4 gap-4 w-100`}>
      <ConfirmDialog />
      <VerifAuthorizationDialog loading={loading} />
      <div></div>
      <EditorPopover
        overlayRef={note.overlayRef}
        popoverPengecekanData={note.popoverPengecekanData}
        handleOnClickFoto={note.handleOnClickFoto}
        pengecekanBerangkatDetilData={pengecekanBerangkatDetilData}
        pengecekanKembaliDetilData={pengecekanKembaliDetilData}
        isBerangkat={isBerangkat}
        handleOnClickReload={handleOnClickReload}
        handleOnClickEditNote={note.handleOnClickEditNote}
      />
      <EditorNoteDialog {...note} folderName={getFotoFolderName(headerData)} />
      <EditorFotoUploader
        uploaderRef={note.fotoUploaderRef}
        fotoTargetRef={note.fotoTargetRef}
        headerData={headerData}
        pengecekanBerangkatDetilData={pengecekanBerangkatDetilData}
        pengecekanKembaliDetilData={pengecekanKembaliDetilData}
        onChangeFoto={note.handleOnChangeFotoDetil}
      />
      <EditorSelectorDialogs
        TrailerDialogs={TrailerDialogs}
        kendaraanFilter={config.kendaraanSelectorFilter}
        headerData={headerData}
        {...marking}
        {...selectorDialogs}
      />
      {/* hidden native camera/gallery input for the vehicle photos (tampak depan/kiri/kanan/belakang) */}
      <input
        ref={camera.cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={camera.handleCameraFileChange}
      />
      <EditorHeaderCard
        TopRows={HeaderTopRows}
        lockVehicleOnKembali={config.lockVehicleOnKembali}
        headerData={headerData}
        setHeaderData={setHeaderData}
        headerError={headerError}
        permittedToEdit={permittedToEdit}
        isBerangkat={isBerangkat}
        loading={loading}
        {...selectorDialogs}
      />
      <EditorActionBar
        loading={loading}
        headerData={headerData}
        permittedToEdit={permittedToEdit}
        isPnjTransport={isPnjTransport}
        handleOnClickBack={navigation.handleOnClickBack}
        handleOnClickSaveHeader={navigation.handleOnClickSaveHeader}
        handleOnClickReload={handleOnClickReload}
      />

      <div className="col-start-start gap-4 w-full" id="excludeFromPrint">
        <EditorChecklistSection
          TrailerCheckList={TrailerCheckList}
          listProblemData={listProblemData}
          // a problem resolved from a checklist row is no longer open: drop it from the list
          onProblemResolved={(problemId) =>
            setListProblemData((list) =>
              list.filter((problem) => problem.id !== problemId)
            )
          }
          headerData={headerData}
          isBerangkat={isBerangkat}
          showBerangkat={showBerangkat}
          setShowBerangkat={setShowBerangkat}
          modeAmbilGambar={modeAmbilGambar}
          setModeAmbilGambar={setModeAmbilGambar}
          permittedToEdit={permittedToEdit}
          pengecekanPertama={pengecekanPertama}
          pengecekanBerangkatData={pengecekanBerangkatData}
          setPengecekanBerangkatData={setPengecekanBerangkatData}
          pengecekanKembaliData={pengecekanKembaliData}
          setPengecekanKembaliData={setPengecekanKembaliData}
          pengecekanBerangkatDetilData={pengecekanBerangkatDetilData}
          setPengecekanBerangkatDetilData={setPengecekanBerangkatDetilData}
          pengecekanKembaliDetilData={pengecekanKembaliDetilData}
          setPengecekanKembaliDetilData={setPengecekanKembaliDetilData}
          handleOnClickDeskripsi={handleOnClickDeskripsi}
          handleOnClickFotoRow={note.handleOnClickFotoRow}
          handleOnClickReload={handleOnClickReload}
          handleOnClickSaveCheckList={handleOnClickSaveCheckList}
          loading={loading}
        />
        <FotoSections {...fotoProps} />
      </div>

      {/* SIGNATURE */}
      <EditorSignatureSection
        headerData={headerData}
        isBerangkat={isBerangkat}
        verifSettingPengecekan={verifSettingPengecekan}
        pengecekanBerangkatData={pengecekanBerangkatData}
        pengecekanKembaliData={pengecekanKembaliData}
        {...signature}
        {...signatureValidators}
      />

      <EditorFooterActions loading={loading} {...footerActions} />

      <div className="h-[3rem]"></div>
    </div>
  );
};

export default EditorMain;
